// vg-binding: P7.D-07
// vg-binding: P7.D-63
// vg-binding: P7.D-256
//
// apps/vendor/src/pages/orders/OrderScanPage.tsx
//
// Amendment #8 — scan a labeled order's barcode to confirm
// warehouse handover (vendorStatus labeled → shipped). Optimized for
// continuous scanning: each scan POSTs scan-transfer and appends a ✓/✗ result
// line; BarcodeScannerInput auto-refocuses so the operator keeps scanning.
// Replaces the old navigate-to-filter stub.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { BarcodeScannerInput } from '../../features/orders/BarcodeScannerInput.js';
import { useScanTransfer } from '../../features/orders/api.js';

interface ScanLine {
  barcode: string;
  ok: boolean;
  message: string;
}

export function OrderScanPage(): JSX.Element {
  const { t } = useTranslation('orders');
  const scan = useScanTransfer();
  const [lines, setLines] = useState<ScanLine[]>([]);

  async function handleScan(value: string): Promise<void> {
    try {
      const r = await scan.mutateAsync(value);
      setLines((prev) => [
        {
          barcode: value,
          ok: true,
          message: r.alreadyShipped ? t('scan.alreadyShipped') : t('scan.shipped'),
        },
        ...prev,
      ]);
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : t('scan.failed');
      setLines((prev) => [{ barcode: value, ok: false, message: msg }, ...prev]);
    }
  }

  return (
    <main
      data-testid="orders-scan-page-root"
      className="flex flex-col gap-6 px-6 py-6"
    >
      <PageHeader
        title={t('scan.title')}
        actions={
          <Link
            to="/orders"
            className="rounded-md border border-pw-border bg-pw-bg px-3 py-1.5 text-sm font-medium text-pw-text hover:bg-pw-surface"
          >
            {t('scan.backToList')}
          </Link>
        }
      />

      <section className="mx-auto w-full max-w-xl rounded-xl border border-pw-border bg-pw-bg p-6 shadow-sm">
        <p className="mb-4 text-sm text-pw-text-muted">
          {t('scan.instructionPre')}
          <code className="font-mono">ORD-XXXX</code>
          {t('scan.instructionPost')}
        </p>
        <BarcodeScannerInput onScan={(value) => void handleScan(value)} />

        {/* Continuous-scan result log — newest first. */}
        {lines.length > 0 ? (
          <ul
            data-testid="scan-result-log"
            className="mt-4 flex max-h-72 flex-col gap-1 overflow-auto text-sm"
          >
            {lines.map((l, i) => (
              <li
                key={`${l.barcode}-${i}`}
                className={
                  l.ok
                    ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-700'
                    : 'rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-red-700'
                }
              >
                <span className="font-mono">{l.barcode}</span> — {l.message}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </main>
  );
}

export default OrderScanPage;
